/**
 * Выбор исполнителя.
 *
 * Появляется между адресом и товарами, и порядок здесь не случаен: кто повезёт
 * — зависит от адреса (его зона должна накрывать точку), а что и почём — от
 * того, кого выбрали. Спрашивать товар раньше исполнителя означало бы
 * показывать цены, которые потом изменятся.
 *
 * Закрытые сегодня исполнители из списка НЕ прячутся, а помечаются: заказ
 * можно оформить и на другой день, а спрятанный исполнитель выглядит как
 * «меня почему-то нет» и для клиента, и для самого исполнителя.
 */
import { useEffect, useState } from 'react';
import type { Address, CurrentUser, VendorPublic } from '@arbuz/shared';
import { api } from '../api';
import { useCart } from '../cart';
import { formatMoney, makeT } from '../i18n';
import { haptic } from '../tg';
import { BottomAction, ClientTopBar, IconCircle, SummaryRow } from './clientUi';

type Load =
  | { status: 'loading' }
  | { status: 'ready'; items: VendorPublic[] }
  | { status: 'error' };

export function VendorSelect({
  user,
  address,
  onBack,
  onPicked,
  onChangeAddress,
}: {
  user: CurrentUser;
  address: Address | null;
  onBack: () => void;
  onPicked: (vendor: VendorPublic) => void;
  onChangeAddress: () => void;
}) {
  const t = makeT(user.locale);
  const cart = useCart();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    if (!address) return;
    let cancelled = false;
    setLoad({ status: 'loading' });

    api
      .get<{ items: VendorPublic[] }>(`/api/vendors?lat=${address.lat}&lon=${address.lon}`)
      .then((res) => {
        if (!cancelled) setLoad({ status: 'ready', items: res.items });
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [address]);

  /** Смена исполнителя чистит корзину — но только с согласия человека. */
  function pick(vendor: VendorPublic) {
    if (cart.vendorId !== null && cart.vendorId !== vendor.id && cart.count > 0) {
      if (!window.confirm(t('vendorSwitchConfirm'))) return;
    }
    haptic('light');
    cart.switchVendor(vendor.id);
    onPicked(vendor);
  }

  if (!address) {
    return (
      <main className="client-page with-bottom-bar">
        <ClientTopBar title={t('vendorsTitle')} onBack={onBack} />
        <section className="client-card client-summary-card">
          <SummaryRow icon="location" label={t('addressTitle')}>
            {t('noAddressYet')}
          </SummaryRow>
        </section>
        <BottomAction>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={onChangeAddress}
          >
            {t('addAddress')}
          </button>
        </BottomAction>
      </main>
    );
  }

  if (load.status === 'loading') {
    return (
      <main className="client-page">
        <ClientTopBar title={t('vendorsTitle')} onBack={onBack} />
        <p className="muted">{t('loading')}</p>
      </main>
    );
  }

  if (load.status === 'error') {
    return (
      <main className="client-page with-bottom-bar">
        <ClientTopBar title={t('vendorsTitle')} onBack={onBack} />
        <h1 className="h1">{t('vendorsLoadError')}</h1>
        <BottomAction>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={() => window.location.reload()}
          >
            {t('retry')}
          </button>
        </BottomAction>
      </main>
    );
  }

  // Пусто — это ответ «сюда не возят», а не сбой: клиент двигает адрес по
  // карте, и промах мимо зоны ошибкой не является.
  if (load.items.length === 0) {
    return (
      <main className="client-page with-bottom-bar">
        <ClientTopBar title={t('vendorsTitle')} onBack={onBack} />
        <section className="client-card client-summary-card">
          <SummaryRow icon="location" label={t('addressTitle')}>
            {address.address_text}
          </SummaryRow>
        </section>
        <div className="client-card" style={{ padding: 'var(--s-4)', marginTop: 'var(--s-3)' }}>
          <p style={{ margin: 0 }}>{t('vendorsEmpty')}</p>
          <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-sm)' }}>
            {t('vendorsEmptyHint')}
          </p>
          {/* Покрытие считается по точке, а не по тексту адреса: подпись «Ттз»
              у пина в четырёх километрах от ТТЗ выглядит правдой, но ею не
              является — и без этой строки человек винит исполнителя */}
          <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-sm)' }}>
            {t('vendorsEmptyPointHint')}
          </p>
        </div>
        <BottomAction>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={onChangeAddress}
          >
            {t('changeAddress')}
          </button>
        </BottomAction>
      </main>
    );
  }

  const allClosed = load.items.every((v) => !v.works_today);

  return (
    <main className="client-page">
      <ClientTopBar title={t('vendorsTitle')} onBack={onBack} />

      <section className="client-card client-summary-card">
        <SummaryRow icon="location" label={t('availableForAddress')}>
          {address.address_text}
        </SummaryRow>
      </section>

      <h1 className="h1" style={{ marginTop: 'var(--s-5)' }}>
        {t('vendorsTitle')}
      </h1>
      <p className="muted" style={{ margin: 'var(--s-2) 0 var(--s-4)' }}>
        {allClosed ? t('vendorsAllClosedHint') : t('vendorsText')}
      </p>

      {allClosed && (
        <div className="client-card" role="status" style={{ padding: 'var(--s-4)', marginBottom: 'var(--s-3)' }}>
          <p style={{ margin: 0 }}>{t('vendorsAllClosed')}</p>
        </div>
      )}

      <section style={{ display: 'grid', gap: 'var(--s-3)' }}>
        {load.items.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => pick(v)}
            className="client-card client-product-card"
            style={{
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              border:
                cart.vendorId === v.id ? '1px solid var(--c-primary-text)' : '1px solid var(--c-border)',
              background: cart.vendorId === v.id ? 'var(--c-primary-subtle)' : undefined,
              font: 'inherit',
              color: 'var(--c-text)',
            }}
          >
            <IconCircle icon="delivery" />
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 'var(--fs-h3)', lineHeight: 'var(--lh-h3)' }}>
                {v.display_name}
              </h2>

              <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
                {[
                  v.min_price && `${t('vendorFrom')} ${formatMoney(v.min_price, user.locale)} ${t('sum')}`,
                  `${t('deliveryPrice')} ${formatMoney(v.delivery_price, user.locale)} ${t('sum')}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>

              <p
                style={{
                  margin: 'var(--s-2) 0 0',
                  fontSize: 'var(--fs-xs)',
                  // Цвет дублирует текст, а не заменяет его
                  color: v.works_today ? 'var(--c-primary-text)' : 'var(--c-text-secondary)',
                }}
              >
                {v.works_today && v.today_hours
                  ? `${t('vendorWorksToday')} ${v.today_hours.end}`
                  : t('vendorClosedToday')}
              </p>
            </div>
          </button>
        ))}
      </section>

      <div style={{ marginTop: 'var(--s-4)' }}>
        <button type="button" className="client-ghost-button" onClick={onChangeAddress}>
          {t('changeAddress')}
        </button>
      </div>
    </main>
  );
}
